import React, { useState } from 'react';
import { formatOrderRef } from '../../utils/orderRef';
import { ChevronDown, ChevronRight, FileText, Download, Eye, User, Calendar, Phone, Mail, MapPin, Box } from 'lucide-react';
import StatusSelect from './StatusSelect';
import NotificationBadge from '../common/NotificationBadge';
import FilePreview from '../common/FilePreview';
import { PROSTHESIS_TYPE_LABELS, getSubtypeLabel } from '../../utils/constants';
import { filesApi } from '../../services/api';

const OrderMobileCard = ({ order, onStatusChange, onOrderClick, onPatientClick, unreadCount = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleDownload = async (file) => {
        if (!file.id) return;
        try {
            const blob = await filesApi.downloadBlob(file.id);
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.name || 'fichier';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Erreur lors du téléchargement du fichier:', error);
        }
    };

    const isPreviewable = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'stl'].includes(extension);
    };

    const needsDigitalModel = order.works?.some(w => w.hasDigitalModel);

    return (
        <>
            <div className="glass border border-white/10 rounded-lg overflow-hidden">
                {/* Zone cliquable principale */}
                <div
                    className="p-4 cursor-pointer active:bg-white/[0.08] transition-colors"
                    onClick={() => onOrderClick && onOrderClick(order)}
                >
                    {/* Header : ID + badges + statut */}
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <NotificationBadge count={unreadCount} size="small" />
                            <span className="font-medium text-white">{formatOrderRef(order)}</span>
                        </div>
                        <StatusSelect
                            status={order.status}
                            onChange={(s) => onStatusChange(order.id, s)}
                        />
                    </div>

                    {/* Infos principales */}
                    <div className="space-y-1.5 text-sm">
                        {/* Client */}
                        <div>
                            <div className="text-white font-medium">{order.clientInfo.name}</div>
                            <div className="text-xs text-gray-400">{order.clientInfo.clinic}</div>
                        </div>

                        {/* Patient */}
                        <div className="flex items-center">
                            <User className="h-3.5 w-3.5 mr-1.5 text-gray-500 flex-shrink-0" />
                            {order.patient_name_code ? (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onPatientClick && onPatientClick({
                                            patientNameCode: order.patient_name_code,
                                            clientName: order.client_name
                                        });
                                    }}
                                    className="text-yellow-400 hover:text-yellow-300 hover:underline transition-colors truncate"
                                    title="Voir l'historique des commandes pour ce patient"
                                >
                                    {order.patient_name_code}
                                </button>
                            ) : (
                                <span className="text-gray-500 italic">Non renseigné</span>
                            )}
                        </div>

                        {/* Type de prothèse */}
                        {order.works && order.works.length > 0 && (
                            <div className="text-gray-300">
                                <span className="font-medium">{PROSTHESIS_TYPE_LABELS[order.works[0].prosthesisType]}</span>
                                {needsDigitalModel && (
                                    <span title="Modèle 3D souhaité" className="text-yellow-400">
                                        <Box className="h-4 w-4 inline align-text-bottom ml-1.5" />
                                    </span>
                                )}
                                {order.works[0].prosthesisSubtype && (
                                    <span className="text-gray-400"> - {getSubtypeLabel(order.works[0].prosthesisType, order.works[0].prosthesisSubtype)}</span>
                                )}
                                {order.works.length > 1 && (
                                    <span className="text-yellow-400 ml-1">+{order.works.length - 1} autre{order.works.length > 2 ? 's' : ''}</span>
                                )}
                                {order.works.flatMap(w => w.toothNumbers || []).length > 0 && (
                                    <div className="text-xs text-gray-400 mt-0.5">🦷 {order.works.flatMap(w => w.toothNumbers || []).join(', ')}</div>
                                )}
                            </div>
                        )}

                        {/* Date de livraison */}
                        <div className="flex items-center text-gray-400">
                            <Calendar className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                            Livraison : {formatDate(order.deliveryDate)}
                        </div>
                    </div>
                </div>

                {/* Barre d'actions : bascule détails */}
                <div className="flex items-center justify-end px-4 py-2.5 bg-black/30 border-t border-white/10">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                        className="flex items-center gap-1 text-gray-400 hover:text-gray-200 text-sm p-1.5 rounded transition-colors"
                    >
                        <span>{isExpanded ? 'Masquer' : 'Détails'}</span>
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                </div>

                {/* Contenu expandé */}
                {isExpanded && (
                    <div className="px-4 py-4 bg-white/10 backdrop-blur-xl border-t border-white/10 space-y-4">
                        {/* Informations client */}
                        <div className="space-y-2">
                            <h4 className="font-medium text-white text-sm">Informations du client</h4>
                            <div className="glass border border-white/10 p-3 rounded-md space-y-2">
                                <div className="flex items-center text-sm">
                                    <Phone className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                                    <span className="text-gray-300">{order.clientInfo.phone}</span>
                                </div>
                                <div className="flex items-center text-sm">
                                    <Mail className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                                    <span className="text-gray-300 break-all">{order.clientInfo.email}</span>
                                </div>
                                <div className="flex items-center text-sm">
                                    <MapPin className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                                    <span className="text-gray-300">{order.clientInfo.address}</span>
                                </div>
                            </div>
                        </div>

                        {/* Détails des travaux */}
                        <div className="space-y-2">
                            <h4 className="font-medium text-white text-sm">Travaux ({order.works?.length || 0})</h4>
                            {order.works && order.works.length > 0 ? (
                                <div className="space-y-2">
                                    {order.works.map((work, idx) => (
                                        <div key={idx} className="glass rounded-md border border-white/10 overflow-hidden">
                                            <div className="bg-indigo-900/30 px-3 py-1.5 border-b border-white/10 flex items-center justify-between">
                                                <span className="text-xs text-indigo-300 font-medium">Travail {idx + 1}</span>
                                                <span className="text-xs text-gray-200 font-medium">
                                                    {PROSTHESIS_TYPE_LABELS[work.prosthesisType]}
                                                    {work.prosthesisSubtype && ` - ${getSubtypeLabel(work.prosthesisType, work.prosthesisSubtype)}`}
                                                </span>
                                            </div>
                                            <div className="p-3">
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    <span className="inline-flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded text-xs text-gray-200">
                                                        🦷 {work.toothNumbers?.join(', ') || '-'}
                                                    </span>
                                                    {work.shade && (
                                                        <span className="inline-flex items-center gap-1 bg-blue-900/40 px-2 py-0.5 rounded text-xs text-blue-200">
                                                            🎨 {work.shade}
                                                        </span>
                                                    )}
                                                </div>
                                                {work.description && (
                                                    <p className="text-xs text-gray-400 leading-relaxed">{work.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="glass border border-white/10 p-3 rounded-md text-center text-sm text-gray-500">
                                    Aucun travail défini
                                </div>
                            )}
                        </div>

                        {/* Fichiers joints */}
                        <div className="space-y-2">
                            <h4 className="font-medium text-white text-sm">Fichiers joints</h4>
                            {order.files && order.files.length > 0 ? (
                                <div className="glass border border-white/10 p-3 rounded-md space-y-2">
                                    {order.files.map((file, idx) => (
                                        <div key={idx} className="flex items-center justify-between bg-black/30 border border-white/10 p-2 rounded">
                                            <div className="flex items-center min-w-0">
                                                <FileText className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                                                <span className="text-sm text-gray-300 truncate">{file.name}</span>
                                                <span className="text-xs text-gray-500 ml-2 flex-shrink-0">({file.size})</span>
                                            </div>
                                            <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                                                {isPreviewable(file.name) && (
                                                    <button
                                                        onClick={() => setPreviewFile(file)}
                                                        className="text-yellow-400 hover:text-yellow-300 p-1 rounded hover:bg-yellow-500/20 transition-colors"
                                                        title="Prévisualiser"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDownload(file)}
                                                    className="text-green-400 hover:text-green-300 p-1 rounded hover:bg-green-500/20 transition-colors"
                                                    title="Télécharger"
                                                >
                                                    <Download className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="glass border border-white/10 p-3 rounded-md text-center text-sm text-gray-500">
                                    Aucun fichier joint
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de prévisualisation */}
            {previewFile && (
                <FilePreview
                    file={previewFile}
                    onClose={() => setPreviewFile(null)}
                />
            )}
        </>
    );
};

export default OrderMobileCard;
